import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { ROUTES, formatSpades } from '../../constants';
import {
  authApi,
  clearStoredAuth,
  NOTIFICATIONS_UPDATED_EVENT,
  readStoredToken,
  readStoredUser,
  SPADES_UPDATED_EVENT,
} from '../../services/authApi';
import SpadeInfoModal from './SpadeInfoModal';
import AddSpadesModal from './AddSpadesModal';
import FilterPanel from './FilterPanel';
import NotificationPanel from './NotificationPanel';
import SearchDropdown from './SearchDropdown';
import polarAceLogo from '../../assets/images/polar_ace_no_bg.png';
import './HomeNavBar.css';

const NOTIFICATION_REFRESH_INTERVAL_MS = 30000;

/**
 * Home page navigation bar.
 *
 * Enhanced version of the base NavBar with:
 * - Search bar (center) — hidden in minimal mode
 * - Spades balance (clickable to open Add Spades panel)
 * - Notification bell with badge
 * - Profile dropdown with navigation and logout
 *
 * @param {Object} props
 * @param {number} props.spadesBalance - User's current Spades balance
 * @param {number} props.notificationCount - Number of unread notifications
 * @param {string|null} props.userAvatar - User's profile picture URL
 * @param {string} props.userName - User's display name
 * @param {function} props.onSpadesClick - Handler for Spades balance click
 * @param {function} [props.onLogout] - Optional page-specific cleanup before logout
 * @param {boolean} props.minimal - Hide search bar and filter (for profile/settings pages)
 * @returns {JSX.Element}
 */
function HomeNavBar({
  spadesBalance,
  notificationCount,
  userAvatar,
  userName,
  onSpadesClick,
  onSpadesPurchaseComplete,
  onLogout,
  minimal = false,
  onSearch,
  logoRoute = ROUTES.HOME,
  forceDocumentNavigation = false,
}) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSpadeInfoOpen, setIsSpadeInfoOpen] = useState(false);
  const [isAddSpadesOpen, setIsAddSpadesOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [displaySpadesBalance, setDisplaySpadesBalance] = useState(spadesBalance);
  const [displayNotificationCount, setDisplayNotificationCount] = useState(notificationCount);
  const [notifications, setNotifications] = useState([]);
  const [isNotificationsLoading, setIsNotificationsLoading] = useState(false);
  const [notificationsError, setNotificationsError] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const dropdownRef = useRef(null);
  const filterBtnRef = useRef(null);
  const notificationRef = useRef(null);
  const searchRef = useRef(null);
  const isMountedRef = useRef(false);

  useEffect(() => {
    setDisplaySpadesBalance(spadesBalance);
  }, [spadesBalance]);

  useEffect(() => {
    setDisplayNotificationCount(notificationCount);
  }, [notificationCount]);

  /* Close dropdowns when clicking outside */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(e.target)) {
        setIsNotificationOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setIsSearchOpen(false);
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsDropdownOpen(false);
        setIsNotificationOpen(false);
        setIsSearchOpen(false);
      }
    };

    if (isDropdownOpen || isNotificationOpen || isSearchOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isDropdownOpen, isNotificationOpen, isSearchOpen]);

  useEffect(() => {
    const handleSpadesUpdated = (event) => {
      const nextBalance = event?.detail?.spadeBalance;
      if (typeof nextBalance === 'number' && !Number.isNaN(nextBalance)) {
        setDisplaySpadesBalance(nextBalance);
      }
    };

    window.addEventListener(SPADES_UPDATED_EVENT, handleSpadesUpdated);
    return () => {
      window.removeEventListener(SPADES_UPDATED_EVENT, handleSpadesUpdated);
    };
  }, []);

  useEffect(() => {
    const handleNotificationsUpdated = (event) => {
      const nextCount = event?.detail?.unreadCount;
      if (typeof nextCount === 'number' && !Number.isNaN(nextCount)) {
        setDisplayNotificationCount(nextCount);
      }
    };

    window.addEventListener(NOTIFICATIONS_UPDATED_EVENT, handleNotificationsUpdated);
    return () => {
      window.removeEventListener(NOTIFICATIONS_UPDATED_EVENT, handleNotificationsUpdated);
    };
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadNotificationUnreadCount = useCallback(async () => {
    const token = readStoredToken();
    if (!token) {
      if (isMountedRef.current) {
        setDisplayNotificationCount(0);
        setNotifications([]);
      }
      return 0;
    }

    try {
      const data = await authApi.getNotificationUnreadCount(token);
      const nextCount = Number(data.unreadCount || 0);
      if (isMountedRef.current) {
        setDisplayNotificationCount(nextCount);
      }
      return nextCount;
    } catch (error) {
      return null;
    }
  }, []);

  useEffect(() => {
    const token = readStoredToken();
    if (!token) {
      setDisplayNotificationCount(0);
      setNotifications([]);
      return undefined;
    }

    loadNotificationUnreadCount();
    const intervalId = window.setInterval(
      loadNotificationUnreadCount,
      NOTIFICATION_REFRESH_INTERVAL_MS
    );

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadNotificationUnreadCount();
      }
    };

    const handleWindowFocus = () => {
      loadNotificationUnreadCount();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [loadNotificationUnreadCount]);

  const loadNotifications = useCallback(async () => {
    const token = readStoredToken();
    if (!token) {
      setNotifications([]);
      setDisplayNotificationCount(0);
      return;
    }

    setIsNotificationsLoading(true);
    setNotificationsError('');

    try {
      const data = await authApi.getNotifications({ limit: 30 }, token);
      setNotifications(Array.isArray(data.notifications) ? data.notifications : []);
    } catch (error) {
      console.error('Failed to load notifications:', error);
      setNotifications([]);
      setNotificationsError('Could not load notifications.');
    } finally {
      setIsNotificationsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (minimal) {
      setSearchResults([]);
      setIsSearchOpen(false);
      setIsSearchLoading(false);
      return undefined;
    }

    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery.length < 2) {
      setSearchResults([]);
      setIsSearchOpen(false);
      setIsSearchLoading(false);
      return undefined;
    }

    let cancelled = false;
    setIsSearchLoading(true);

    const timeoutId = window.setTimeout(async () => {
      try {
        const results = await authApi.searchUsers(trimmedQuery);
        if (cancelled) return;
        setSearchResults(Array.isArray(results) ? results : []);
        setIsSearchOpen(true);
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to search users:', error);
        setSearchResults([]);
        setIsSearchOpen(true);
      } finally {
        if (!cancelled) {
          setIsSearchLoading(false);
        }
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [minimal, searchQuery]);

  const handleSearch = (e) => {
    e.preventDefault();
    const trimmedQuery = searchQuery.trim();

    if (trimmedQuery && onSearch) {
      onSearch(trimmedQuery);
    }

    if (trimmedQuery.length >= 2) {
      setIsSearchOpen(true);
    }
  };

  const handleSearchInputChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    if (onSearch) {
      onSearch(val.trim());
    }
    if (val.trim().length >= 2) {
      setIsSearchOpen(true);
    }
  };

  const handleNotificationClick = () => {
    setIsNotificationOpen((wasOpen) => {
      const nextOpen = !wasOpen;
      if (nextOpen) {
        loadNotificationUnreadCount();
        loadNotifications();
      }
      return nextOpen;
    });
  };

  const handleMarkNotificationRead = async (notification) => {
    if (!notification?.id || notification.isRead) {
      return;
    }

    try {
      const data = await authApi.markNotificationRead(notification.id);
      setDisplayNotificationCount(Number(data.unreadCount || 0));
      setNotifications((prev) => prev.map((item) => (
        item.id === notification.id
          ? { ...item, isRead: true, readAt: new Date().toISOString() }
          : item
      )));
    } catch (error) {
      console.error('Failed to mark notification read:', error);
    }
  };

  const handleClearNotifications = async () => {
    try {
      const data = await authApi.clearNotifications();
      setDisplayNotificationCount(Number(data.unreadCount || 0));
      setNotifications([]);
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    }
  };

  const handleNotificationNavigate = async (notification) => {
    await handleMarkNotificationRead(notification);

    const actionUrl = typeof notification?.actionUrl === 'string' ? notification.actionUrl.trim() : '';
    if (!actionUrl) {
      return;
    }

    setIsNotificationOpen(false);
    if (actionUrl.startsWith('/')) {
      if (forceDocumentNavigation) {
        window.location.assign(actionUrl);
        return;
      }
      navigate(actionUrl);
      return;
    }

    window.location.assign(actionUrl);
  };

  const handleSpadeIconClick = () => {
    setIsSpadeInfoOpen(true);
  };

  const handleSpadesBalanceClick = () => {
    setIsAddSpadesOpen(true);
    onSpadesClick();
  };

  const handleGetSpadesFromInfo = () => {
    setIsAddSpadesOpen(true);
  };

  const handleFilterClick = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const handleApplyFilters = (filters) => {
    console.log('Filters applied:', filters);
    // Would apply filters to stream results
  };

  const handleLogout = async () => {
    setIsDropdownOpen(false);

    const initialToken = readStoredToken();

    try {
      await onLogout?.();
    } catch (error) {
      console.error('Logout handler failed:', error);
    }

    const remainingToken = readStoredToken();
    const remainingUser = readStoredUser();

    if (remainingToken || remainingUser || !onLogout) {
      await authApi.logout(remainingToken || initialToken || undefined);
      clearStoredAuth();
      if (forceDocumentNavigation) {
        window.location.assign(ROUTES.WELCOME);
        return;
      }
      navigate(ROUTES.WELCOME);
    }
  };

  const renderRouteLink = (path, className, children, extraProps = {}) => {
    if (forceDocumentNavigation) {
      return (
        <a href={path} className={className} {...extraProps}>
          {children}
        </a>
      );
    }

    return (
      <Link to={path} className={className} {...extraProps}>
        {children}
      </Link>
    );
  };

  const formatNotificationCount = (count) => {
    if (count > 999) return '999+';
    return count.toString();
  };

  const SearchIcon = () => (
    <svg width="23" height="19" viewBox="0 0 23 19" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M20.3039 17.6266C19.6739 17.1866 19.0739 16.6866 18.4539 16.2266C16.5139 14.7866 14.564 13.3366 12.584 11.9466C12.534 11.9166 12.5438 11.8466 12.4738 11.9066C11.8538 12.7566 10.9739 13.3966 9.99387 13.7866C7.78387 14.6666 5.03384 14.5366 3.09384 13.0966C-0.986159 10.0666 -1.06607 4.28664 3.06393 1.29664C6.10393 -0.903359 10.4039 -0.203359 12.7539 2.63664C13.7539 3.84664 14.3839 5.70664 14.3739 7.26664C14.3639 8.42664 13.9839 9.50664 13.4439 10.5166C14.5339 11.2866 15.6139 12.0566 16.6839 12.8366C18.0739 13.8466 19.444 14.8866 20.834 15.8966C21.114 16.1066 21.8639 16.5366 22.0439 16.7766C22.4839 17.3466 21.7939 18.3266 21.0739 18.1066C20.8839 18.0466 20.4839 17.7466 20.3139 17.6266H20.3039ZM12.4839 5.48664C11.5439 1.97664 7.19396 0.706638 4.21396 2.51664C1.56396 4.12664 0.913856 7.57664 2.48386 10.1966C3.97386 12.6866 7.51387 13.5466 9.99387 12.0166C12.3139 10.5866 13.1939 8.09664 12.4939 5.48664H12.4839Z" fill="#D5EFF7"/>
    </svg>
  );

  const FilterIcon = () => (
    <svg width="20" height="15" viewBox="0 0 25 19" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M24.5647 14.6992C24.6547 15.1292 24.6948 15.7591 24.1248 15.7991H8.8147C7.6747 18.9791 3.64478 19.2092 2.52478 15.8892L0.164673 15.6292C0.0746728 15.4792 0.0646973 15.3192 0.0646973 15.1492C0.0646973 14.8592 0.0947656 14.6091 0.384766 14.5192L2.51477 14.3992C3.26477 12.2692 5.67469 11.3092 7.55469 12.7192C8.23469 13.2292 8.38472 14.3792 8.96472 14.4892C13.7247 14.7492 18.8547 14.1392 23.5647 14.4892C23.9147 14.5192 24.2447 14.5492 24.5647 14.7092V14.6992ZM5.07471 13.4091C3.05471 13.8691 3.47474 17.2892 5.73474 17.0692C8.31474 16.8292 7.80471 12.7791 5.07471 13.4091Z" fill="#E4F5FD" stroke="#D5EFF6" strokeWidth="0.05" strokeMiterlimit="10"/>
      <path d="M24.5648 3.60906C24.2548 3.76906 23.9148 3.79906 23.5648 3.82906C18.8548 4.16906 13.7149 3.55906 8.95486 3.81906C8.47486 3.91906 8.20482 5.09905 7.54482 5.58905C6.30482 6.50905 4.59486 6.45906 3.45486 5.39906C2.99486 4.96906 2.74492 4.04905 2.51492 3.90905C2.15492 3.68905 1.14487 3.86905 0.594872 3.76905C0.424872 3.73905 0.264892 3.67906 0.114892 3.60906C0.044892 3.26906 -0.00507867 2.66905 0.394921 2.53906L2.52493 2.42905C3.29493 -0.190945 6.57487 -0.870944 8.21487 1.39906C8.44487 1.71906 8.46483 2.36906 8.80483 2.48906L24.1249 2.50906C24.6949 2.54906 24.6548 3.17906 24.5648 3.60906ZM5.45486 1.23906C2.96486 1.46906 3.31486 5.32905 5.95486 4.93905C8.14486 4.60905 7.78486 1.02906 5.45486 1.23906Z" fill="#E4F5FD" stroke="#D5EFF6" strokeWidth="0.05" strokeMiterlimit="10"/>
      <path d="M14.2947 8.49902C14.8047 6.24902 17.5148 5.24901 19.3348 6.73901C20.0248 7.29901 20.0248 7.90902 20.5948 8.46902C21.7148 8.84902 25.2448 7.61902 24.4748 9.63902L20.3047 9.82902C19.9547 12.169 17.0748 12.959 15.3248 11.499C14.7448 11.019 14.6448 10.349 14.1848 9.83901L0.924743 9.81902C-0.035257 9.83902 -0.235194 9.19901 0.364807 8.50901H14.3047L14.2947 8.49902ZM18.7248 7.81902C16.9548 6.03902 14.2647 8.72902 16.0447 10.499C17.7147 12.159 20.3848 9.49902 18.7248 7.81902Z" fill="#E4F5FD" stroke="#D5EFF6" strokeWidth="0.05" strokeMiterlimit="10"/>
    </svg>
  );

  const SpadeIcon = () => (
    <svg width="18" height="21" viewBox="0 0 24 28" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M13.4618 16.6661L14.9018 26.5661L8.3818 26.5461C9.0518 23.2661 9.72181 19.9761 10.3918 16.6861C10.0518 17.1161 9.41181 17.8061 8.39181 18.3061C7.81181 18.5961 6.21171 19.2661 4.29171 18.7561C3.91171 18.6561 1.6117 18.0461 0.781702 15.9561C0.241702 14.5961 0.611712 13.3461 0.791712 12.7461C1.10171 11.7461 1.58176 11.1261 3.71176 8.93609C5.80176 6.78609 7.00172 5.55609 8.55172 4.12609C8.55172 4.12609 10.1818 2.64609 11.2418 1.45609C11.2418 1.44609 11.8818 0.746094 11.8818 0.746094C12.5518 1.49609 13.4418 2.48609 14.5018 3.62609C15.4618 4.65609 16.7418 6.01609 18.4618 7.72609C19.3518 8.60609 19.9418 9.17609 20.8518 10.1861C21.1618 10.5261 21.5718 10.9061 22.0018 11.5261C22.1018 11.6661 22.4018 12.0861 22.5818 12.4461C23.2618 13.8361 22.6617 15.4661 22.5517 15.7461C22.4017 16.1561 21.7618 17.7961 19.9618 18.5861C18.5018 19.2261 17.1618 18.9361 16.7418 18.8361C16.2518 18.6961 15.5517 18.4261 14.8217 17.9361C14.1817 17.5061 13.7317 17.0361 13.4317 16.6761L13.4618 16.6661Z" fill="#4CC8EF" stroke="#8DD6EF" strokeMiterlimit="10"/>
    </svg>
  );

  const BELL_PATH_IDLE = "M34.9073 29.4056C33.6173 27.5456 32.3374 25.7056 31.0574 23.8856L30.9574 23.7056C30.9174 22.1656 30.8974 20.6056 30.8874 19.0356C30.9074 17.9956 30.8574 16.9656 30.7074 15.9456C30.2174 12.4156 27.6974 9.86563 27.4974 9.66563C25.7674 8.00563 23.9674 7.45563 23.5473 7.33563C22.9774 7.17563 21.3774 6.77563 19.3674 7.16563C18.8174 7.27563 17.3374 7.61563 15.8974 8.61563C12.1374 11.2356 11.2775 16.3856 11.2775 18.4256C11.2775 20.0856 11.3074 21.7456 11.3274 23.4056C11.1474 23.7556 10.8773 24.0656 10.6573 24.3956C10.2573 25.0056 9.86738 25.6256 9.45738 26.2256C8.79738 27.1956 8.02734 28.1656 7.40734 29.1556C6.71734 29.8956 6.89742 31.2456 7.99742 31.3056C10.2174 31.3556 12.5273 31.3856 14.7873 31.3456L15.5774 31.3656C15.6774 31.3656 15.7473 31.4156 15.7873 31.5156C16.7173 35.1456 20.5274 37.4756 23.8074 35.3256C25.1074 34.5156 26.0074 32.9656 26.3874 31.5056C26.4174 31.3856 26.4874 31.3556 26.6074 31.3556C29.0074 31.3956 31.4374 31.4156 33.8574 31.3856C34.0074 31.3856 34.1474 31.3856 34.2973 31.3556C35.3174 31.3056 35.4774 30.1056 34.9274 29.3756L34.9073 29.4056ZM23.2574 32.7756C21.7174 34.1456 19.5374 33.9956 18.3874 32.1956C18.2874 32.0456 18.1473 31.7056 18.1573 31.5256L23.9674 31.6056C23.9274 32.0456 23.5674 32.4956 23.2574 32.7756ZM31.7775 29.0056C31.3275 28.9856 30.8773 28.9656 30.4173 28.9756C26.0473 29.0856 21.6874 29.1256 17.3174 29.0656C15.5674 29.0456 13.7973 29.0256 12.0473 28.9756C11.4873 28.9556 10.9274 28.9256 10.3674 28.9156C10.3674 28.8556 10.3674 28.8256 10.3974 28.7756C10.5974 28.3356 11.2275 27.4856 11.5275 27.0756C12.0575 26.3456 12.5774 25.6156 13.0874 24.8756C13.4274 24.4056 13.5274 23.8656 13.5174 23.2856C13.4774 21.7356 13.4574 20.1756 13.4574 18.6156C13.4774 17.3356 13.5374 16.0556 13.8774 14.8356C15.0874 11.0756 18.6074 8.88563 22.3674 9.50563C24.6274 9.87563 27.0474 11.9456 27.9674 14.1256C28.3874 15.1256 28.4074 16.4156 28.4674 17.5056C28.5674 19.3456 28.6774 21.1956 28.5874 23.0356C28.5474 23.8456 28.3474 24.0056 28.8274 24.7456C29.6374 25.9856 30.5974 27.1956 31.4374 28.4456C31.4874 28.5156 31.8174 28.9756 31.7674 29.0156L31.7775 29.0056Z";

  const BELL_PATH_ACTIVE = "M34.9073 29.4056C33.6173 27.5456 32.3374 25.7056 31.0574 23.8856L30.9574 23.7056C30.9174 22.1656 30.8974 20.6056 30.8874 19.0356C30.9074 17.9956 30.8574 16.9656 30.7074 15.9456C30.2174 12.4156 27.6974 9.86563 27.4974 9.66563C25.7674 8.00563 23.9674 7.45563 23.5473 7.33563C22.9774 7.17563 21.3774 6.77563 19.3674 7.16563C18.8174 7.27563 17.3374 7.61563 15.8974 8.61563C12.1374 11.2356 11.2775 16.3856 11.2775 18.4256C11.2775 20.0856 11.3074 21.7456 11.3274 23.4056C11.1474 23.7556 10.8773 24.0656 10.6573 24.3956C10.2573 25.0056 9.86738 25.6256 9.45738 26.2256C8.79738 27.1956 8.02734 28.1656 7.40734 29.1556C6.71734 29.8956 6.89742 31.2456 7.99742 31.3056C10.2174 31.3556 12.5273 31.3856 14.7873 31.3456L15.5774 31.3656C15.6774 31.3656 15.7473 31.4156 15.7873 31.5156C16.7173 35.1456 20.5274 37.4756 23.8074 35.3256C25.1074 34.5156 26.0074 32.9656 26.3874 31.5056C26.4174 31.3856 26.4874 31.3556 26.6074 31.3556C29.0074 31.3956 31.4374 31.4156 33.8574 31.3856C34.0074 31.3856 34.1474 31.3856 34.2973 31.3556C35.3174 31.3056 35.4774 30.1056 34.9274 29.3756L34.9073 29.4056ZM23.2574 32.7756C21.7174 34.1456 19.5374 33.9956 18.3874 32.1956C18.2874 32.0456 18.1473 31.7056 18.1573 31.5256L23.9674 31.6056C23.9274 32.0456 23.5674 32.4956 23.2574 32.7756Z";

  const BellIcon = ({ active }) => (
    <svg width="32" height="33" viewBox="0 0 49 50" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <g filter="url(#bell-filter)">
        <path
          d={active ? BELL_PATH_ACTIVE : BELL_PATH_IDLE}
          fill={active ? '#4CC8EF' : '#D5EFF7'}
        />
      </g>
      <defs>
        <filter id="bell-filter" x="0" y="0" width="48.2388" height="49.1504" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix"/>
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
          <feOffset dx="3" dy="3"/>
          <feGaussianBlur stdDeviation="5"/>
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.5 0"/>
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow"/>
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape"/>
        </filter>
      </defs>
    </svg>
  );

  return (
    <nav className="home-nav" role="navigation" aria-label="Main navigation">
      {/* Left Section: Logo */}
      <div className="home-nav__left">
        {renderRouteLink(
          logoRoute,
          'home-nav__logo',
          <img src={polarAceLogo} alt="Polar Ace" />
        )}
      </div>

      {/* Center Section: Search — hidden in minimal mode */}
      {!minimal && (
        <div className="home-nav__center">
          <div className="home-nav__search-outer" ref={searchRef}>
            <div className="home-nav__search-container">
              <form className="home-nav__search" onSubmit={handleSearch}>
                <input
                  type="text"
                  className="home-nav__search-input"
                  placeholder="Search for a Streamer"
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  onFocus={() => {
                    if (searchQuery.trim().length >= 2) {
                      setIsSearchOpen(true);
                    }
                  }}
                  aria-label="Search for a streamer"
                />
              </form>
              <button
                type="button"
                className="home-nav__search-btn"
                onClick={handleSearch}
                aria-label="Search"
              >
                <SearchIcon />
              </button>
            </div>
            <SearchDropdown
              results={searchResults}
              isLoading={isSearchLoading}
              isVisible={isSearchOpen}
              query={searchQuery.trim()}
              onClose={() => setIsSearchOpen(false)}
            />
          </div>
          <div className="home-nav__filter-wrapper" ref={filterBtnRef}>
            <button
              type="button"
              className="home-nav__filter-btn"
              onClick={handleFilterClick}
              aria-label="Filter"
              aria-expanded={isFilterOpen}
            >
              <FilterIcon />
            </button>
            <FilterPanel
              isOpen={isFilterOpen}
              onClose={() => setIsFilterOpen(false)}
              onApplyFilters={handleApplyFilters}
            />
          </div>
        </div>
      )}

      {/* Right Section: Spades, Notifications, Profile */}
      <div className="home-nav__right">
        {/* Spades Balance — split: icon opens info, balance opens purchase */}
        <div className="home-nav__spades">
          <button
            type="button"
            className="home-nav__spades-info"
            onClick={handleSpadeIconClick}
            aria-label="What is a Spade?"
          >
            <span className="home-nav__spades-icon"><SpadeIcon /></span>
          </button>
          <button
            type="button"
            className="home-nav__spades-balance"
            onClick={handleSpadesBalanceClick}
            aria-label={`${formatSpades(displaySpadesBalance)} Spades. Click to add more.`}
          >
            {formatSpades(displaySpadesBalance)}
          </button>
        </div>

        {/* Notifications */}
        <div className="home-nav__notification-wrapper" ref={notificationRef}>
          <button
            type="button"
            className="home-nav__notification"
            onClick={handleNotificationClick}
            aria-label={`${displayNotificationCount} notifications`}
            aria-expanded={isNotificationOpen}
          >
            <BellIcon active={isNotificationOpen} />
            {displayNotificationCount > 0 && (
              <span className="home-nav__notification-badge">
                {formatNotificationCount(displayNotificationCount)}
              </span>
            )}
          </button>
          <NotificationPanel
            isOpen={isNotificationOpen}
            notifications={notifications}
            isLoading={isNotificationsLoading}
            errorMessage={notificationsError}
            unreadCount={displayNotificationCount}
            onClose={() => setIsNotificationOpen(false)}
            onClearAll={handleClearNotifications}
            onNotificationClick={handleNotificationNavigate}
          />
        </div>

        {/* Profile Dropdown */}
        <div className="home-nav__profile" ref={dropdownRef}>
          <button
            type="button"
            className="home-nav__avatar"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            aria-expanded={isDropdownOpen}
            aria-haspopup="true"
            aria-label="User menu"
          >
            {userAvatar ? (
              <img src={userAvatar} alt="" />
            ) : (
              <span className="home-nav__avatar-placeholder">
                {userName.charAt(0).toUpperCase()}
              </span>
            )}
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="home-nav__dropdown" role="menu">
              {/* User header */}
              <div className="home-nav__dropdown-header">
                {userAvatar ? (
                  <img src={userAvatar} alt="" className="home-nav__dropdown-avatar" />
                ) : (
                  <span className="home-nav__dropdown-avatar-placeholder">
                    {userName ? userName.charAt(0).toUpperCase() : '?'}
                  </span>
                )}
                <span className="home-nav__dropdown-username">{userName}</span>
              </div>
              <div className="home-nav__dropdown-rule" />
              {renderRouteLink(
                ROUTES.PROFILE,
                'home-nav__dropdown-item',
                'Your Profile',
                { role: 'menuitem', onClick: () => setIsDropdownOpen(false) }
              )}
              {renderRouteLink(
                ROUTES.SETTINGS,
                'home-nav__dropdown-item',
                'Settings',
                { role: 'menuitem', onClick: () => setIsDropdownOpen(false) }
              )}
              {renderRouteLink(
                ROUTES.WALLET,
                'home-nav__dropdown-item',
                'Wallet',
                { role: 'menuitem', onClick: () => setIsDropdownOpen(false) }
              )}
              {renderRouteLink(
                ROUTES.SUPPORT,
                'home-nav__dropdown-item',
                'Support',
                { role: 'menuitem', onClick: () => setIsDropdownOpen(false) }
              )}
              {renderRouteLink(
                ROUTES.REPORTS,
                'home-nav__dropdown-item',
                'Report History',
                { role: 'menuitem', onClick: () => setIsDropdownOpen(false) }
              )}
              <button
                type="button"
                className="home-nav__dropdown-item home-nav__dropdown-item--logout"
                role="menuitem"
                onClick={handleLogout}
              >
                Log Out
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <SpadeInfoModal
        isOpen={isSpadeInfoOpen}
        onClose={() => setIsSpadeInfoOpen(false)}
        onGetSpades={handleGetSpadesFromInfo}
      />

      <AddSpadesModal
        isOpen={isAddSpadesOpen}
        onClose={() => setIsAddSpadesOpen(false)}
        onPurchaseComplete={(result) => {
          setIsAddSpadesOpen(false);
          onSpadesPurchaseComplete?.(result);
        }}
      />
    </nav>
  );
}

HomeNavBar.propTypes = {
  spadesBalance: PropTypes.number.isRequired,
  notificationCount: PropTypes.number.isRequired,
  userAvatar: PropTypes.string,
  userName: PropTypes.string.isRequired,
  onSpadesClick: PropTypes.func.isRequired,
  onSpadesPurchaseComplete: PropTypes.func,
  onLogout: PropTypes.func,
  minimal: PropTypes.bool,
  onSearch: PropTypes.func,
  logoRoute: PropTypes.string,
  forceDocumentNavigation: PropTypes.bool,
};

export default HomeNavBar;
